<template>
  <form action="#" v-stack-y:sm @submit.prevent="handleRegister" class="slo-auth-register">
    <SloField>
      <SloLabel>{{ 'First Name' | t }}</SloLabel>
      <SloInput
        ref="input"
        name="firstName"
        v-model="form.firstName"
        type="text"
        inputmode="name"
        required
      />
    </SloField>
    <SloField>
      <SloLabel>{{ 'Last Name' | t }}</SloLabel>
      <SloInput
        name="lastName"
        v-model="form.lastName"
        type="text"
        inputmode="name"
        required
      />
    </SloField>
    <SloField>
      <SloLabel>{{ 'Email' | t  }}</SloLabel>
      <SloInput
        id="email"
        name="email"
        v-model="form.email"
        type="email"
        inputmode="email"
        required
      />
    </SloField>
    <SloField>
      <SloLabel :error="displayConfirmEmailError">{{ 'Confirm Email' | t  }}</SloLabel>
      <SloInput
        name="confirmEmail"
        v-model="form.confirmEmail"
        :error="displayConfirmEmailError && 'Emails must match' | t"
        type="email"
        inputmode="email"
        required
        @blur="displayConfirmEmailError = form.email !== form.confirmEmail"
      />
    </SloField>
    <SloField>
      <SloLabel>{{ 'Password' | t }}</SloLabel>
      <SloInput
        name="password"
        v-model="form.password"
        type="password"
        @blur="displayConfirmPasswordErrorCheck"
        required
      />
    </SloField>
    <SloPasswordValidation
      :password="form.password"
      :confirm-password="form.confirmPassword"
      @status="handlePasswordValidationStatus"
    />
    <SloField>
      <SloLabel :error="displayConfirmPasswordError">{{ 'Confirm Password' | t }}</SloLabel>
      <SloInput
        name="confirmPassword"
        v-model="form.confirmPassword"
        :error="displayConfirmPasswordError && 'Passwords must match' | t"
        @blur="displayConfirmPasswordErrorCheck"
        type="password"
        required
      />
    </SloField>
    <div class="slo-auth-extra">
      <SloField>
        <SloInput
          v-model="form.marketingEmailSignup"
          :checked="form.marketingEmailSignup"
          type="checkbox"
        />
        <SloLabel>{{ 'Yes, sign me up to receive emails from Sloan' | t  }}</SloLabel>
      </SloField>
      <div id="clickwrap-container-auth"></div>
      <div class="recatpcha-container">
        <div id="g-recaptcha" class="g-recaptcha" :data-sitekey="recaptchaSiteKey"></div>
      </div>
    </div>
    <SloButton :disabled="isSubmitDisabled" type="submit">
      <SloLoader :loading="loading" color="white" size="md">
        {{ 'Create Account' | t }}
      </SloLoader>
    </SloButton>
  </form>
</template>

<script>
import {mapGetters, mapActions} from 'vuex';
import t from '../../js/main/i18n';
import {handleApiErrors, initializeClickwrap, marketingEmailSignup} from '../../js/main/utils';
import SloField from './SloField.vue';
import SloLabel from './SloLabel.vue';
import SloInput from './SloInput.vue';
import SloButton from './SloButton.vue';
import SloLoader from './SloLoader.vue';
import SloPasswordValidation from './SloPasswordValidation.vue';

export default {
  name: 'SloAuthRegister',
  components: {
    SloField,
    SloLabel,
    SloInput,
    SloButton,
    SloLoader,
    SloPasswordValidation
  },
  filters: {
    t
  },
  props: {
    email: {type: String, default: ''},
    marketingEmailSignup: {type: Boolean, default: true}
  },
  data() {
    return {
      loading: false,
      passwordValidated: false,
      confirmPasswordValidated: false,
      displayConfirmEmailError: false,
      displayConfirmPasswordError: false,
      recaptchaSiteKey: '',
      recaptchaWidgetId: '',
      clickwrapGroup: 'sloan-registration',
      clickwrapValidated: false,
      form: {
        firstName: '',
        lastName: '',
        email: '',
        confirmEmail: '',
        password: '',
        confirmPassword: '',
        passwordValid: false,
        marketingEmailSignup: true,
        token: ''
      }
    };
  },
  created() {
    this.form.email = this.email;
    this.form.marketingEmailSignup = this.marketingEmailSignup;
    this.recaptchaSiteKey = document.head.querySelector('meta[name="marketplace-recaptcha-site-key"]').content;

    const recaptchaScript = document.createElement('script');
    recaptchaScript.setAttribute('async', '');
    recaptchaScript.setAttribute('defer', '');
    recaptchaScript.setAttribute('src', 'https://www.google.com/recaptcha/api.js?render=explicit');
    recaptchaScript.onload = () => window.grecaptcha.ready(() => this.mountRecaptcha());
    document.head.appendChild(recaptchaScript);

    if (this.$refs.input) {
      this.$nextTick(() => this.$refs.input.setFocus());
    }
  },
  mounted() {
    initializeClickwrap(this.clickwrapGroup, 'clickwrap-container-auth');

    if (window._ps) {
      window._ps.on('valid', () => this.clickwrapValidated = true);
      window._ps.on('unchecked', () => {
        this.clickwrapValidated = false;
        window._ps(`${this.clickwrapGroup}:send`, 'disagreed');
      });
    } else {
      this.clickwrapValidated = true;
    }
  },
  computed: {
    ...mapGetters('auth', ['authenticated']),
    isSubmitDisabled() {
      return this.loading
        || !this.form.firstName
        || !this.form.lastName
        || !this.form.email
        || this.form.email !== this.form.confirmEmail
        || !this.passwordValidated
        || !this.confirmPasswordValidated
        || !this.clickwrapValidated
        || !this.form.token;
    }
  },
  methods: {
    ...mapActions('auth', ['register']),
    handlePasswordValidationStatus(status) {
      this.passwordValidated = status.password;
      this.confirmPasswordValidated = status.confirmPassword;
    },
    displayConfirmPasswordErrorCheck() {
      if (this.form.confirmPassword) {
        this.displayConfirmPasswordError = !this.confirmPasswordValidated;
      }
    },
    mountRecaptcha() {
      this.recaptchaWidgetId = window.grecaptcha.render('g-recaptcha', {
        sitekey: this.recaptchaSiteKey,
        callback: () => {
          this.form.token = window.grecaptcha.getResponse(this.recaptchaWidgetId);
        },
        'expired-callback': () => {
          this.form.token = '';
          window.grecaptcha.reset(this.recaptchaWidgetId);
        }
      });
    },
    async handleRegister() {
      if (this.authenticated) {
        return this.$emit('success');
      }

      this.loading = true;

      if (
        window._ps &&
        window._ps.getByKey(this.clickwrapGroup) &&
        window._ps.getByKey(this.clickwrapGroup).block()
      ) {
        this.loading = false;
        this.$snotify.error(t('Please accept the terms and conditions.'));
        return;
      }

      await this.register(this.form)
        .then(() => {
          window._ps('send', 'updated', {
            custom_data: {
              site: 'Sloan Marketplace',
              first_name: this.form.firstName,
              last_name: this.form.lastName
            }
          });

          if (this.form.marketingEmailSignup) {
            marketingEmailSignup(this.form);
          }

          this.$emit('success');
        })
        .catch(e => {
          window.scrollTo(0, 0);
          this.loading = false;
          handleApiErrors(e, this, 'Unable to register your account');
        });
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../scss/system';

.slo-auth-register {
  width: 100%;
  margin-block-start: 3.2rem;

  .recatpcha-container {
    display: flex;
    justify-content: center;
    margin-block-start: 1.6rem;

    #g-recaptcha {
      width: 100% !important;

      > div {
        width: 100% !important;
      }
    }
  }
}

.slo-button {
  width: 100%;
}

.slo-auth-extra {
  :deep(.slo-field) {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  .slo-label {
    margin-top: 0 !important;
  }
}

:deep(div.ps-contract) {
  padding-bottom: 0 !important;
}
</style>
